import { useMutation } from '@tanstack/react-query';

import { useUserStore } from '@/entities/user/user-store';

import { UseRPCMutationHandlers } from '@/lib/api/models';
import { basicJsonRPCRequest } from '@/lib/api/request/basic-jsonrpc-request';

export type ConsentAction = 'grant' | 'revoke';

export type FormType =
  | 'login'
  | 'register'
  | 'user_details_self'
  | 'user_details_guest'
  | 'guest_details'
  | 'review'
  | 'legal_update'
  | 'cookie'
  | 'payment'
  | 'order_prepayment';

export type LogConsentRequest = {
  action?: ConsentAction;
  formType: FormType;
  signedDocuments: string[];
  userId?: string | null;
  checkboxesState?: Record<string, boolean>;
};

export type LogConsentResponse = {
  success: boolean;
  consentLogId?: string;
  createdAt?: string;
};

function buildCheckboxesState(
  signedDocuments: string[],
  action: ConsentAction = 'grant',
): Record<string, boolean> {
  const isGrant = action === 'grant';
  return signedDocuments.reduce<Record<string, boolean>>((acc, documentKey) => {
    acc[documentKey] = isGrant;
    return acc;
  }, {});
}

export function useLogConsent(
  handlers?: UseRPCMutationHandlers<LogConsentResponse, LogConsentRequest>,
) {
  const userId = useUserStore((state) => state.userId) ?? '';

  return useMutation({
    mutationKey: [useLogConsent.method] as const,
    mutationFn: (params: LogConsentRequest) => {
      const action = params.action ?? 'grant';
      const checkboxesState =
        params.checkboxesState ?? buildCheckboxesState(params.signedDocuments, action);

      return basicJsonRPCRequest<LogConsentResponse>({
        method: useLogConsent.method,
        params: {
          ...params,
          action,
          checkboxesState,
        },
        skipCache: true,
        userId,
      });
    },
    ...handlers,
  });
}

useLogConsent.method = 'user:log-consent';
